// src/services/api.service.ts
import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import Cookies from 'js-cookie'

export interface ApiResponse<R> {
  status: number
  data: R | undefined
  mesage?:string
  error?:string|unknown
}

// only allow simple serializable query‑param values
export type QueryParams = Record<string, string | number | boolean | undefined>

// --- shared defaults ---

const defaultConfig: AxiosRequestConfig = {
  baseURL: process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000/api/',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
}

// Helper to get token from localStorage (client-side only)
function getToken() {
  if (typeof window === 'undefined') return null;
  const user = localStorage.getItem('user');
  console.log(user)
  if (!user) return null;
  try {
    const parsed = JSON.parse(user);
    // Support both {token, user: {...}} and {accessToken}
    return parsed?.token || parsed?.accessToken || null;
  } catch {
    return null;
  }
}

// centralized error handler
async function handleError<R>(err: AxiosError): Promise<ApiResponse<R>> {
  const status = err.response?.status ?? 500
  if (status === 403) {
    Cookies.remove('enrgyUser')
    // window.location.replace('/login')
  }
  // we cast here because err.response.data may be anything,
  // but the caller has declared what R should be.
  return {
    status,
    data: (err.response?.data as { data?: R })?.data,
    error: (err.response?.data as { error?: string })?.error
  }
}

// --- GET ---
export async function getAPI<
  R = unknown,
  Q extends QueryParams = QueryParams
>(
  url: string,
  params?: Q
): Promise<ApiResponse<R>> {
  try {
    const token = getToken();
    console.log("token ")
    console.log(token)
    const headers = token
      ? { ...defaultConfig.headers, Authorization: `Bearer ${token}` }
      : defaultConfig.headers;
    const resp = await axios.request<R>({
      ...defaultConfig,
      method: 'get',
      url,
      params,
      headers,
    })
    return { status: resp.status, data: resp.data }
  } catch (e) {
    return handleError<R>(e as AxiosError)
  }
}

// --- POST ---
export async function postAPI<
  R = unknown,
  B = unknown,
  Q extends QueryParams = QueryParams
>(
  url: string,
  body?: B,
  params?: Q
): Promise<ApiResponse<R>> {
  try {
    const token = getToken();
    const headers = token
      ? { ...defaultConfig.headers, Authorization: `Bearer ${token}` }
      : defaultConfig.headers;
    const resp = await axios.request<R>({
      ...defaultConfig,
      method: 'post',
      url,
      data: body,
      params,
      headers,
    })
    if (resp.status === 403) {
      Cookies.remove('user')
    //   window.location.replace('/login')
    }
    return { status: resp.status, data: resp.data }
  } catch (e) {
    return handleError<R>(e as AxiosError)
  }
}

// --- PATCH ---
export async function patchAPI<
  R = unknown,
  B = unknown,
  Q extends QueryParams = QueryParams
>(
  url: string,
  body?: B,
  params?: Q
): Promise<ApiResponse<R>> {
  try {
    const token = getToken();
    const headers = token
      ? { ...defaultConfig.headers, Authorization: `Bearer ${token}` }
      : defaultConfig.headers;
    const resp = await axios.request<R>({
      ...defaultConfig,
      method: 'patch',
      url,
      data: body,
      params,
      headers,
    })
    return { status: resp.status, data: resp.data }
  } catch (e) {
    return handleError<R>(e as AxiosError)
  }
}

// --- DELETE ---
export async function deleteAPI<
  R = unknown,
  Q extends QueryParams = QueryParams
>(
  url: string,
  params?: Q
): Promise<ApiResponse<R>> {
  try {
    const token = getToken();
    const headers = token
      ? { ...defaultConfig.headers, Authorization: `Bearer ${token}` }
      : defaultConfig.headers;
    const resp = await axios.request<R>({
      ...defaultConfig,
      method: 'delete',
      url,
      params,
      headers,
    })
    return { status: resp.status, data: resp.data }
  } catch (e) {
    return handleError<R>(e as AxiosError)
  }
}

export const isoToDate = (iso?: string) =>
  iso ? iso.split("T")[0] : "";